import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Loader, Phone, Mic, MicOff } from 'lucide-react';
import toast from 'react-hot-toast';
import { useSupabase } from '../hooks/useSupabase';
import type { Database } from '../lib/supabase';
import FeedbackForm from './FeedbackForm';
import { initializeVapi } from '../utils/vapiScript';

declare global {
  interface Window {
    vapiInstance: any;
  }
}

type Interviewer = Database['public']['Tables']['interviewers']['Row'];

export default function Interview() {
  const { id } = useParams();
  const { updateLastActive, client: supabase } = useSupabase();
  const [interviewer, setInterviewer] = useState<Interviewer | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(false);
  const [isCallActive, setIsCallActive] = useState(false);
  const [vapiStatus, setVapiStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [initializationError, setInitializationError] = useState<string | null>(null);
  const [isMuted, setIsMuted] = useState(false);

  const stopInterview = () => {
    if (window.vapiInstance) {
      window.vapiInstance.stop();
      setIsCallActive(false);
    }
  };

  const startInterview = async () => {
    if (!interviewer?.vapi_assistant_id || !interviewer?.vapi_api_key) {
      setInitializationError('Missing API key or assistant ID');
      return;
    }

    setIsInitializing(true);
    setVapiStatus('loading');

    try {
      const { error } = await supabase
        .from('interviewers')
        .update({ 
          calls_count: (interviewer.calls_count || 0) + 1,
          consents_count: (interviewer.consents_count || 0) + 1,
          last_active: new Date().toISOString()
        })
        .eq('id', interviewer.id);

      if (error) throw error;

      const vapi = await initializeVapi(
        interviewer.vapi_api_key,
        interviewer.vapi_assistant_id
      );

      // Start the call
      await vapi.start(interviewer.vapi_assistant_id);
      
      setVapiStatus('ready');
      setIsCallActive(true);
      updateLastActive(interviewer.id).catch(console.error);
    } catch (error) {
      console.error('Failed to start interview:', error);
      setInitializationError(error instanceof Error ? error.message : 'Failed to start interview');
      setVapiStatus('error');
      toast.error('Failed to start the interview. Please try again.');
    } finally {
      setIsInitializing(false);
    }
  };

  const toggleMute = () => {
    if (window.vapiInstance) {
      const newMutedState = !isMuted;
      window.vapiInstance.setMuted(newMutedState);
      setIsMuted(newMutedState);
      toast.success(newMutedState ? 'Microphone muted' : 'Microphone unmuted', {
        duration: 2000
      });
    }
  };

  useEffect(() => {
    loadInterviewer();
    
    return () => {
      if (window.vapiInstance) {
        window.vapiInstance.stop();
      }
    };
  }, []);

  const loadInterviewer = async () => {
    setIsLoading(true);
    setError(null);
    const currentPath = window.location.pathname;

    try {
      const { data, error: dbError } = await supabase
        .from('interviewers')
        .select('*')
        .ilike('url', `%${currentPath}`)
        .single();

      if (dbError) {
        console.error('Database error:', dbError);
        throw new Error('Failed to load interview details');
      }

      if (!data) {
        throw new Error('Interview not found');
      }

      if (!data.vapi_assistant_id || !data.vapi_api_key) {
        throw new Error('Interview configuration is incomplete');
      }

      setInterviewer(data);
    } catch (error) {
      console.error('Error in loadInterviewer:', error);
      setError(error instanceof Error ? error.message : 'An unknown error occurred');
      toast.error(error instanceof Error ? error.message : 'Failed to load interview details');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900">Loading interview...</h2>
        </div>
      </div>
    );
  }

  if (error || !interviewer) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <h2 className="text-xl font-semibold text-gray-900">
            {error || 'Interview not found'}
          </h2>
          <p className="mt-2 text-gray-600">
            This interview link may be invalid or expired.
          </p>
          {process.env.NODE_ENV === 'development' && error && (
            <pre className="mt-4 p-4 bg-red-50 text-red-900 text-sm rounded-md overflow-auto">
              {error}
            </pre>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <h1 className="text-lg font-semibold text-gray-900">
            {interviewer.company} - AI Interview
          </h1>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row gap-8">
          <div className="lg:flex-1">
            <div className="relative w-full min-h-[400px] bg-white rounded-lg shadow-lg p-8">
              {!isCallActive && (
                <div className="text-center mt-4">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">
                    Ready to Start Your Interview
                  </h2>
                  <div className="max-w-lg mx-auto mb-8 space-y-4">
                    <p className="text-gray-600">
                      This is a proof of concept. When we build your AI interviewer, it will be fully custom to your phone screen structure, data, and judgment.
                    </p>
                    <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                      <ul className="text-sm text-blue-700 space-y-2">
                        <li>• Ensure you're in a quiet environment</li>
                        <li>• Use headphones for better audio quality</li>
                        <li>• Your browser will request microphone access</li>
                        <li>• Speak clearly and at a normal pace</li>
                      </ul>
                    </div>
                  </div>
                  <button
                    onClick={startInterview}
                    disabled={isInitializing}
                    className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-brand-primary hover:bg-brand-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary"
                  >
                    {isInitializing ? (
                      <>
                        <Loader className="w-5 h-5 mr-2 animate-spin" />
                        Connecting...
                      </>
                    ) : (
                      <>
                        <Phone className="w-5 h-5 mr-2" />
                        Start Interview
                      </>
                    )}
                  </button>
                  {interviewer.phone_number && (
                    <p className="text-gray-600 mt-4">
                      You can also start an interview by calling{' '}
                      <a 
                        href={`tel:${interviewer.phone_number}`}
                        className="text-brand-primary hover:underline font-medium"
                      >
                        {interviewer.phone_number}
                      </a>
                    </p>
                  )}
                  {isInitializing && (
                    <div className="flex items-center justify-center space-x-2 mt-4">
                      <Loader className="w-4 h-4 animate-spin" />
                      <span>Loading interview system...</span>
                    </div>
                  )}
                  {vapiStatus === 'error' && initializationError && (
                    <div className="text-red-600 mt-4">
                      <p>Error: {initializationError}</p>
                      <button 
                        onClick={startInterview}
                        className="text-blue-600 hover:underline mt-2"
                      >
                        Click to retry
                      </button>
                    </div>
                  )}
                </div>
              )}
              {isCallActive && window.vapiInstance && (
                <div className="text-center mt-4">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">
                    Interview in Progress
                  </h2>
                  <button
                    onClick={stopInterview}
                    className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                    disabled={!isCallActive}
                  >
                    <Phone className="w-5 h-5 mr-2" />
                    End Interview
                  </button>
                  <button
                    onClick={toggleMute}
                    className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 ml-4"
                  >
                    {isMuted ? (
                      <MicOff className="w-5 h-5 mr-2" />
                    ) : (
                      <Mic className="w-5 h-5 mr-2" />
                    )}
                    {isMuted ? 'Unmute' : 'Mute'}
                  </button>
                </div>
              )}
            </div>
          </div>
          
          <div className="lg:w-96">
            <FeedbackForm interviewerId={interviewer.id} />
          </div>
        </div>
      </main>
    </div>
  );
}